import React, { useMemo } from 'react';
import root from 'react-shadow';
import { Box } from '../ui';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

interface TiptapViewerProps {
  content: string;
  sx?: any;
}

// Shadow DOM isolates banner styles (e.g. inline `style="background:..."` on a
// wrapper div) from the rest of the app, and vice versa — `.App` font/color
// inheritance can't bleed in either direction.
//
// IMPORTANT: keep the content rules below in sync with the ProseMirror sx in
// TiptapEditor so what authors see while editing matches what learners see
// when reading. We deliberately avoid an "apply on import" step — imported
// HTML stays as-is and the viewer is the single source of typographic truth.
const shadowStyles = `
  :host {
    all: initial;
    display: block;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 16px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.87);
  }
  /* Reset the app/UA cascade for imported HTML — but never inside <svg>.
     'all: revert' rolls back author-origin styles, and SVG presentation
     attributes (fill="none", stroke="currentColor") are author-origin, so
     reverting them turns line icons into solid black blobs. Excluding the
     svg subtree keeps those attributes intact. */
  *:not(svg):not(svg *) { all: revert; box-sizing: border-box; }

  /* Block spacing — give every block element room to breathe so imported
     HTML (PVA, pasted content) doesn't read as a wall of text. */
  p, ul, ol, blockquote, pre, table { margin: 0.75em 0; }
  h1, h2, h3, h4, h5, h6 { margin: 1em 0 0.5em; line-height: 1.3; font-weight: 600; }
  *:first-child { margin-top: 0; }
  *:last-child { margin-bottom: 0; }

  /* Headings */
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1.1em; }

  /* Lists — generous indent + per-item spacing so bullets don't run together. */
  ul, ol { padding-left: 1.75em; }
  li { margin-bottom: 0.4em; }
  li > ul, li > ol { margin: 0.4em 0; }
  li:last-child { margin-bottom: 0; }

  /* Links */
  a { color: #1976d2; text-decoration: underline; cursor: pointer; }
  a:hover { text-decoration: none; }

  /* Tables (often present in PVA imports) */
  table { border-collapse: collapse; width: auto; }
  th, td { padding: 0.5em 0.75em; border: 1px solid #e0e0e0; vertical-align: top; }
  th { background: #f5f5f5; font-weight: 600; text-align: left; }

  /* Blockquote */
  blockquote {
    margin-left: 0;
    border-left: 4px solid #e0e0e0;
    padding: 0.25em 0 0.25em 1em;
    color: rgba(0, 0, 0, 0.7);
  }

  /* Code */
  code { background: rgba(0, 0, 0, 0.05); padding: 0.15em 0.35em; border-radius: 3px; font-family: monospace; }
  pre { background: rgba(0, 0, 0, 0.05); padding: 0.75em 1em; border-radius: 5px; overflow-x: auto; }
  pre code { background: transparent; padding: 0; }

  /* Prevent oversized images from blowing out the layout. */
  img { max-width: 100%; height: auto; }
`;

// Workspace banners are produced by TiptapEditor
// (which emits plain HTML); account banners are admin-set via SQL. Both render
// identically — no schema normalization, no Tiptap involved on the read path.
const TiptapViewer: React.FC<TiptapViewerProps> = ({ content, sx = {} }) => {
  const safeHtml = useMemo(() => sanitizeHtml(content), [content]);
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <root.div>
        <style>{shadowStyles}</style>
        <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
      </root.div>
    </Box>
  );
};

export default TiptapViewer;
